import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { StorageService } from '../../../core/services/storage.service';
import { TestSeriesApiEndpoints } from '../../../shared/enum/api-endpoints.enum';
import { parseBackendDateArray } from '../../../shared/utils/date.util';
import { embedImagesInHtml, normalizeImageUrls } from '../../../shared/utils/question-html.util';
import {
  ActiveExamMeta,
  ResultConfig,
  CompleteExamsApiResponse,
  ExamDetailsApiData,
  ExamQuestionApiItem,
  ExamResultApiResponse,
  ExamSettings,
  ExamTestPartsConfig,
  GetTestSeriesPayload,
  GetTestSeriesResponse,
  PreparedQuestionPaperState,
  QuestionPaperItem,
  QuestionPart,
  QuestionResultApiResponse,
  ResumeData,
  SectionQuestionMapApiResponse,
  StartExamResponse,
  SubmitExamPayload,
} from '../model/test-series.model';

@Injectable({
  providedIn: 'root',
})
export class TestSeriesService {
  private static readonly FIXED_PART_COUNT = 5;
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(StorageService);
  private readonly apiUrl = environment.apiUrl;
  /** How long one exam-details response is reused across the start flow. */
  private static readonly EXAM_DETAILS_CACHE_MS = 60_000;
  private readonly examDetailsCache = new Map<string, Observable<ExamDetailsApiData>>();

  getAllTestSeries(payload: GetTestSeriesPayload): Observable<GetTestSeriesResponse> {
    return this.http.post<GetTestSeriesResponse>(
      `${this.apiUrl}/${TestSeriesApiEndpoints.GET_ALL_TEST_SERIES}`,
      payload,
    );
  }

  getExamQuestions(examId: string): Observable<ExamQuestionApiItem[]> {
    return this.http.get<ExamQuestionApiItem[]>(
      `${this.apiUrl}/examinations/${examId}/${TestSeriesApiEndpoints.GET_EXAM_QUESTIONS_SUFFIX}`,
    );
  }

  /**
   * The start popup, exam-loading guard, instructions page and question paper
   * all need the same details within seconds of each other, so one request is
   * shared (including while it is still in flight) for a short window.
   * Failed requests are not cached.
   */
  getExamDetails(examId: string): Observable<ExamDetailsApiData> {
    const cached = this.examDetailsCache.get(examId);
    if (cached) {
      return cached;
    }

    const request$ = this.http
      .get<ExamDetailsApiData>(`${this.apiUrl}/examinations/${examId}`)
      .pipe(
        tap({
          next: () =>
            setTimeout(
              () => this.evictExamDetails(examId, request$),
              TestSeriesService.EXAM_DETAILS_CACHE_MS,
            ),
          error: () => this.evictExamDetails(examId, request$),
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.examDetailsCache.set(examId, request$);
    return request$;
  }

  private evictExamDetails(examId: string, request$: Observable<ExamDetailsApiData>): void {
    if (this.examDetailsCache.get(examId) === request$) {
      this.examDetailsCache.delete(examId);
    }
  }

  /** Features stay off unless the exam details API explicitly enables them. */
  extractExamSettings(examDetails: ExamDetailsApiData | null | undefined): ExamSettings {
    return {
      enableCalculator: examDetails?.enableCalculator === true,
      enableNegativeMarking: examDetails?.enableNegativeMarking === true,
      shuffleQuestions: examDetails?.shuffleQuestions === true,
      enableMagnifier: examDetails?.enableMagnifier === true,
      enableProctoring: examDetails?.enableProctoring === true,
    };
  }

  startExam(examId: string): Observable<StartExamResponse> {
    return this.http.post<StartExamResponse>(
      `${this.apiUrl}/examinations/${examId}/${TestSeriesApiEndpoints.START_EXAM_SUFFIX}`,
      {},
    );
  }

  /** Best-effort extraction — tolerates either camelCase or snake_case from the backend. */
  extractAttemptId(response: StartExamResponse | null | undefined): string | undefined {
    return response?.data?.attemptId || response?.data?.attempt_id || undefined;
  }

  submitExam(payload: SubmitExamPayload): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/${TestSeriesApiEndpoints.SUBMIT_EXAM}`,
      payload,
    );
  }

  completeExams(examIds: string[]): Observable<CompleteExamsApiResponse> {
    return this.http.patch<CompleteExamsApiResponse>(
      `${this.apiUrl}/${TestSeriesApiEndpoints.COMPLETE_EXAMINATIONS}`,
      { exam_ids: examIds },
    );
  }

  getExamResult(examId: string): Observable<ExamResultApiResponse> {
    return this.http.post<ExamResultApiResponse>(
      `${this.apiUrl}/${TestSeriesApiEndpoints.GET_EXAM_RESULT}`,
      { exam_id: examId },
    );
  }

  getSectionQuestionMap(examId: string): Observable<SectionQuestionMapApiResponse> {
    return this.http.get<SectionQuestionMapApiResponse>(
      `${this.apiUrl}/examinations/${examId}/${TestSeriesApiEndpoints.GET_SECTION_QUESTION_MAP_SUFFIX}`,
    );
  }

  getQuestionResult(examId: string, questionId: string): Observable<QuestionResultApiResponse> {
    return this.http.post<QuestionResultApiResponse>(
      `${this.apiUrl}/${TestSeriesApiEndpoints.GET_QUESTION_RESULT}`,
      { exam_id: examId, question_id: questionId },
    );
  }

  prepareQuestionPaperState(
    examId: string,
    apiQuestions: ExamQuestionApiItem[],
    examMeta: ActiveExamMeta | null,
    settings: ExamSettings,
    testParts?: ExamTestPartsConfig | null,
    resultConfig?: ResultConfig | null,
  ): PreparedQuestionPaperState {
    const durationInMinutes = examMeta?.durationInMinutes ?? 0;
    // Questions are shown in the order the backend sends them.
    const parts = this.buildExamParts(apiQuestions.length, durationInMinutes, testParts);
    const totalDurationInMinutes = parts.reduce((sum, part) => sum + part.durationInMinutes, 0);
    const questions = this.buildPreparedQuestions(apiQuestions, parts, settings);
    const resumeData = examMeta?.isResume
      ? this.buildResumeData(apiQuestions, questions, parts, examMeta)
      : undefined;

    return {
      examId,
      examTitle: examMeta?.examTitle ?? 'Examination',
      examMeta: `${apiQuestions.length} Questions · ${totalDurationInMinutes || '-'} min`,
      totalDurationInMinutes,
      parts,
      questions,
      resumeData,
      expiryTimestamp: examMeta?.expiryTimestamp ?? null,
      timeZone: examMeta?.timeZone,
      resultConfig: resultConfig ?? null,
      settings,
    };
  }

  getStoredUserId(): string {
    const directUserId = this.storageService.getItem<string>('userId');

    if (typeof directUserId === 'string' && directUserId.trim()) {
      return directUserId.trim();
    }

    const storedUser = this.storageService.getItem<{ user_id?: string; userId?: string; id?: string }>('userData');
    return storedUser?.user_id?.trim() || storedUser?.userId?.trim() || storedUser?.id?.trim() || '';
  }

  buildExamParts(
    totalQuestions: number,
    durationInMinutes: number,
    testParts?: ExamTestPartsConfig | null,
  ): QuestionPart[] {
    const configuredParts = this.buildConfiguredParts(totalQuestions, testParts);

    if (configuredParts.length) {
      return configuredParts;
    }

    const partCount = this.getPartCount(totalQuestions);
    return this.buildParts(totalQuestions, durationInMinutes, partCount);
  }

  /**
   * Parts configured by the admin (name, timing, question count), in
   * `partNumber` order. Question counts are fitted to the questions actually
   * returned so no question is dropped and no part is left empty.
   */
  private buildConfiguredParts(
    totalQuestions: number,
    testParts?: ExamTestPartsConfig | null,
  ): QuestionPart[] {
    if (!testParts?.enabled || !testParts.parts?.length) {
      return [];
    }

    const sortedParts = [...testParts.parts].sort((a, b) => a.partNumber - b.partNumber);
    const hasQuestionTotal = totalQuestions > 0;
    let remainingQuestions = totalQuestions;

    const parts = sortedParts.map((part, index) => {
      const configuredCount = Math.max(part.questionCount || 0, 0);
      const isLastPart = index === sortedParts.length - 1;
      let questionCount = configuredCount;

      if (hasQuestionTotal) {
        questionCount = isLastPart ? remainingQuestions : Math.min(configuredCount, remainingQuestions);
        remainingQuestions -= questionCount;
      }

      return {
        id: `part-${part.partNumber ?? index + 1}`,
        label: part.partName?.trim() || `Part ${part.partNumber ?? index + 1}`,
        info: `${questionCount} Questions`,
        durationInMinutes: Math.max(part.durationMinutes || 0, 0),
      };
    });

    // Without a question total the configured counts are shown as-is;
    // otherwise drop parts that ended up with no questions.
    return hasQuestionTotal ? parts.filter((part) => Number.parseInt(part.info, 10) > 0) : parts;
  }

  private getPartCount(totalQuestions: number): number {
    return totalQuestions > 0 ? TestSeriesService.FIXED_PART_COUNT : 1;
  }

  private buildParts(totalQuestions: number, durationInMinutes: number, partCount: number): QuestionPart[] {
    const baseQuestionsPerPart = Math.floor(totalQuestions / partCount);
    const questionRemainder = totalQuestions % partCount;
    const baseDurationPerPart = Math.floor(durationInMinutes / partCount);
    const durationRemainder = durationInMinutes % partCount;

    return Array.from({ length: partCount }, (_, index) => {
      const questionCount = baseQuestionsPerPart + (index < questionRemainder ? 1 : 0);
      const partDuration = baseDurationPerPart + (index < durationRemainder ? 1 : 0);
      const partLabel = `Group ${String.fromCharCode(65 + index)}`;

      return {
        id: `part-${String.fromCharCode(97 + index)}`,
        label: partLabel,
        info: `${questionCount} Questions`,
        durationInMinutes: partDuration,
      };
    });
  }

  private buildPreparedQuestions(
    apiQuestions: ExamQuestionApiItem[],
    parts: QuestionPart[],
    settings: ExamSettings,
  ): QuestionPaperItem[] {
    const questionCountByPart = parts.map((part) => Number.parseInt(part.info, 10) || 0);
    let runningIndex = 0;

    return apiQuestions.map((question, index) => {
      while (
        runningIndex < questionCountByPart.length - 1
        && index >= questionCountByPart
          .slice(0, runningIndex + 1)
          .reduce((sum, count) => sum + count, 0)
      ) {
        runningIndex += 1;
      }

      // The API may send one URL, an array, a JSON-array string or a comma list.
      // Images go into their <img> placeholders in the HTML (e.g. a table cell);
      // only URLs without a placeholder are rendered below the question.
      const { html: prompt, remainingImageUrls } = embedImagesInHtml(
        question.question,
        normalizeImageUrls(question.question_image_url),
      );

      return {
        id: index + 1,
        part: parts[runningIndex]?.label ?? parts[0]?.label ?? 'Group A',
        type: question.question_type || 'MCQ',
        prompt,
        // Guess tracker only matters when wrong answers cost marks.
        isGuessingAllowed: settings.enableNegativeMarking,
        media: remainingImageUrls.map((src, imageIndex, urls) => ({
          src,
          alt: urls.length > 1
            ? `Question ${index + 1} image ${imageIndex + 1}`
            : `Question ${index + 1} image`,
        })),
        options: question.options.map((option) => ({
          id: `${question.id}-${option.key.toLowerCase()}`,
          label: option.key,
          text: option.text,
        })),
        subject: question.subject,
        chapter: question.chapter,
        topic: question.topic,
        explanation: question.explanation,
        correctAnswer: question.correct_answer,
        sourceQuestionId: question.id,
      };
    });
  }

  private buildResumeData(
    apiQuestions: ExamQuestionApiItem[],
    preparedQuestions: QuestionPaperItem[],
    parts: QuestionPart[],
    examMeta: ActiveExamMeta,
  ): ResumeData {
    const savedAnswers: Record<number, string> = {};
    const selectedAnswers: Record<number, string> = {};
    const visitedQuestions: Record<number, boolean> = {};
    const markedForReview: Record<number, boolean> = {};
    const guessedQuestions: Record<number, boolean> = {};

    // Build maps from API question data
    apiQuestions.forEach((apiQ, index) => {
      const preparedQ = preparedQuestions[index];
      if (!preparedQ) return;

      const questionId = preparedQ.id;

      if (apiQ.is_question_answered && apiQ.selected_answer) {
        const matchingOption = preparedQ.options.find(
          (opt) => opt.label === apiQ.selected_answer!.key,
        );
        if (matchingOption) {
          savedAnswers[questionId] = matchingOption.id;
          selectedAnswers[questionId] = matchingOption.id;
        }
        visitedQuestions[questionId] = true;
      }

      if (apiQ.marked_for_review) {
        markedForReview[questionId] = true;
        visitedQuestions[questionId] = true;
      }

      if (apiQ.is_guessing) {
        guessedQuestions[questionId] = true;
      }

      // The backend is the source of truth: a question it reports as visited
      // (is_not_visited === false) stays visited even if it was never answered
      // or flagged as skipped.
      if (apiQ.is_not_visited === false || (apiQ.is_skipped && apiQ.is_not_visited !== true)) {
        visitedQuestions[questionId] = true;
      }
    });

    // Calculate elapsed time and determine resume part
    const startedAt = examMeta.startedAt;
    const totalDurationSeconds = examMeta.durationInMinutes * 60;
    let elapsedSeconds = 0;

    if (startedAt && startedAt.length >= 6) {
      const startTime = parseBackendDateArray(startedAt, examMeta.timeZone);
      elapsedSeconds = Math.max(Math.floor((Date.now() - startTime) / 1000), 0);
    }

    const totalTimeLeftInSeconds = Math.max(totalDurationSeconds - elapsedSeconds, 0);

    // Find which part the user should resume from based on elapsed time
    let resumePartIndex = 0;
    let cumulativeSeconds = 0;

    for (let i = 0; i < parts.length; i++) {
      const partDurationSeconds = Math.round(parts[i].durationInMinutes * 60);
      cumulativeSeconds += partDurationSeconds;

      if (elapsedSeconds < cumulativeSeconds) {
        resumePartIndex = i;
        break;
      }

      // If we've gone past all parts, land on the last one
      if (i === parts.length - 1) {
        resumePartIndex = i;
      }
    }

    // Calculate time left for the resume part
    const cumulativeBeforeResumePart = parts
      .slice(0, resumePartIndex)
      .reduce((sum, p) => sum + Math.round(p.durationInMinutes * 60), 0);
    const resumePartDurationSeconds = Math.round(parts[resumePartIndex].durationInMinutes * 60);
    const elapsedInResumePart = Math.max(elapsedSeconds - cumulativeBeforeResumePart, 0);
    const partTimeLeftInSeconds = Math.max(resumePartDurationSeconds - elapsedInResumePart, 0);

    // Mark all questions in elapsed parts as visited
    for (let i = 0; i < resumePartIndex; i++) {
      const partLabel = parts[i]?.label;
      preparedQuestions
        .filter((q) => q.part === partLabel)
        .forEach((q) => {
          visitedQuestions[q.id] = true;
        });
    }

    // Find the furthest visited question in the resume part
    const resumePartLabel = parts[resumePartIndex]?.label;
    const resumePartQuestions = preparedQuestions.filter((q) => q.part === resumePartLabel);
    
    let resumeQuestionIndex = 0;
    for (let i = resumePartQuestions.length - 1; i >= 0; i--) {
      if (visitedQuestions[resumePartQuestions[i].id]) {
        resumeQuestionIndex = i;
        break;
      }
    }

    return {
      savedAnswers,
      selectedAnswers,
      visitedQuestions,
      markedForReview,
      guessedQuestions,
      resumePartIndex,
      resumeQuestionIndex,
      totalTimeLeftInSeconds,
      partTimeLeftInSeconds,
    };
  }
}
